import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Crosshair, Swords, Trophy, Film, FileText, HardDrive, Play, Square,
    Radio, Brain, ChevronRight,
} from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, Tooltip } from "recharts";
import { PageHeader } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { ScoreGauge } from "@/components/common/ScoreGauge";
import { Loader } from "@/components/common/Loader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { api, fileUrl } from "@/lib/api";

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [games, setGames] = useState([]);
    const [game, setGame] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const [d, g] = await Promise.all([api.get("/dashboard"), api.get("/games")]);
        setData(d.data);
        setGames(g.data.games);
        setGame(d.data.current_game?.id || g.data.games[0].id);
    };
    useEffect(() => { load(); }, []);

    const toggleMonitoring = async () => {
        setBusy(true);
        try {
            await api.put("/session", { monitoring: !data.monitoring, current_game: game });
            await load();
            toast.success(!data.monitoring ? "Monitoring started — AI is watching the buffer." : "Monitoring stopped.");
        } finally { setBusy(false); }
    };

    if (!data) return <Loader label="Loading command center" />;
    const spark = (data.spark || []).map((v, i) => ({ i, v }));

    return (
        <div className="ww-fade-up">
            <PageHeader title="Command Center" subtitle="Your live coaching overview for this session." />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard testid="stat-improvement" label="Improvement Score" value={data.improvement_score}
                    sub="this week" delta={data.weekly_growth} icon={Crosshair} accent="#00C2FF" />
                <StatCard testid="stat-kd" label="K / D Ratio" value={data.stats.kd} sub={`${data.stats.kills} kills`} icon={Swords} accent="#FF3366" />
                <StatCard testid="stat-winrate" label="Win Rate" value={`${data.stats.win_rate}%`} sub={`${data.stats.matches} matches`} icon={Trophy} accent="#00E676" />
                <StatCard testid="stat-monthly" label="Monthly Growth" value={`${data.monthly_growth >= 0 ? "+" : ""}${data.monthly_growth}`} sub="score pts" icon={Brain} accent="#FFB000" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                {/* Monitoring control */}
                <div className="lg:col-span-2 bg-surface border border-border rounded-sm p-6" data-testid="monitoring-panel">
                    <div className="flex items-start justify-between mb-5">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Screen Recording Engine</div>
                            <div className="font-heading text-xl font-bold mt-1">Live Capture & AI Detection</div>
                        </div>
                        <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-sm border text-xs font-mono uppercase ${data.monitoring ? "text-success border-success/40 bg-success/10" : "text-muted-foreground border-border"}`}>
                            <span className={`w-2 h-2 rounded-full ${data.monitoring ? "bg-success ww-live-dot" : "bg-muted-foreground"}`} />
                            {data.ai_status}
                        </span>
                    </div>

                    <div className="grid sm:grid-cols-3 gap-4 mb-5">
                        <div className="bg-obsidian border border-border rounded-sm p-3">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Rolling Buffer</div>
                            <div className="font-mono text-lg text-white mt-1">{data.buffer_seconds}s</div>
                        </div>
                        <div className="bg-obsidian border border-border rounded-sm p-3">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Detected Game</div>
                            <div className="font-mono text-lg text-white mt-1 truncate">{data.current_game?.short || "—"}</div>
                        </div>
                        <div className="bg-obsidian border border-border rounded-sm p-3">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Storage</div>
                            <div className="font-mono text-lg text-white mt-1">{data.storage.used_gb}GB</div>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                        <Select value={game} onValueChange={setGame}>
                            <SelectTrigger className="bg-obsidian border-border rounded-sm h-11 flex-1" data-testid="dashboard-game-select">
                                <Radio className="w-4 h-4 text-volt mr-1.5" />
                                <SelectValue placeholder="Select game" />
                            </SelectTrigger>
                            <SelectContent className="bg-surface2 border-border">
                                {games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <button data-testid="toggle-monitoring" disabled={busy} onClick={toggleMonitoring}
                            className={`h-11 px-6 rounded-sm font-semibold flex items-center justify-center gap-2 transition-colors ${data.monitoring ? "bg-alert text-white hover:bg-[#ff4d7a]" : "bg-volt text-black hover:bg-[#33ceff]"}`}>
                            {data.monitoring ? <><Square className="w-4 h-4" /> Stop</> : <><Play className="w-4 h-4" /> Start Monitoring</>}
                        </button>
                    </div>

                    {spark.length > 0 && (
                        <div className="mt-5 h-20 -mx-1">
                            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                                <AreaChart data={spark}>
                                    <defs>
                                        <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#00C2FF" stopOpacity={0.4} />
                                            <stop offset="100%" stopColor="#00C2FF" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <Tooltip contentStyle={{ background: "#1A1A1A", border: "1px solid #333", borderRadius: 2, fontSize: 12 }} />
                                    <Area type="monotone" dataKey="v" stroke="#00C2FF" strokeWidth={2} fill="url(#sparkGrad)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>

                {/* Score + storage */}
                <div className="space-y-6">
                    <div className="bg-surface border border-border rounded-sm p-6 flex flex-col items-center">
                        <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold self-start mb-2">Personal Improvement</div>
                        <ScoreGauge value={data.improvement_score} label="OVERALL" />
                        <div className="flex gap-6 mt-4 text-center">
                            <div><div className="font-mono text-success text-sm">+{data.weekly_growth}</div><div className="text-[10px] text-muted-foreground uppercase">Weekly</div></div>
                            <div><div className="font-mono text-success text-sm">+{data.monthly_growth}</div><div className="text-[10px] text-muted-foreground uppercase">Monthly</div></div>
                        </div>
                    </div>
                    <div className="bg-surface border border-border rounded-sm p-6" data-testid="storage-card">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Storage</span>
                            <HardDrive className="w-4 h-4 text-volt" />
                        </div>
                        <div className="font-mono text-2xl text-white">{data.storage.used_gb}<span className="text-sm text-muted-foreground"> / {data.storage.limit_gb} GB</span></div>
                        <Progress value={data.storage.percent} className="h-1.5 mt-3 bg-obsidian" />
                        <div className="text-xs text-muted-foreground mt-2 font-mono">{data.storage.percent}% used</div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-surface border border-border rounded-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading text-lg font-bold flex items-center gap-2"><Film className="w-4 h-4 text-volt" /> Recent Clips</h3>
                        <Link to="/clips" className="text-xs text-volt hover:underline flex items-center gap-1" data-testid="view-all-clips">View all <ChevronRight className="w-3 h-3" /></Link>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {data.recent_clips.length === 0 && (
                            <div className="col-span-2 sm:col-span-4 text-sm text-muted-foreground py-6 text-center" data-testid="dashboard-no-clips">
                                No clips yet — start a live capture to auto-generate highlights.
                            </div>
                        )}
                        {data.recent_clips.map((c) => {
                            const thumb = c.thumbnail_path ? fileUrl(c.thumbnail_media_url) : c.thumbnail_url;
                            return (
                                <Link key={c.id} to="/clips" className="group relative aspect-video bg-obsidian border border-border rounded-sm overflow-hidden block" data-testid={`clip-${c.id}`}>
                                    {thumb
                                        ? <img src={thumb} alt={c.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                                        : <div className="w-full h-full grid place-items-center ww-grid-bg"><Film className="w-6 h-6 text-muted-foreground" /></div>}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                                    <div className="absolute bottom-1.5 left-2 right-2">
                                        <div className="text-[10px] text-volt font-mono uppercase truncate">{c.event_type}</div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>

                <div className="bg-surface border border-border rounded-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading text-lg font-bold flex items-center gap-2"><FileText className="w-4 h-4 text-volt" /> Recent Reports</h3>
                        <Link to="/coach" className="text-xs text-volt hover:underline flex items-center gap-1">View all <ChevronRight className="w-3 h-3" /></Link>
                    </div>
                    <div className="space-y-3">
                        {data.recent_reports.map((r) => (
                            <div key={r.id} className="border-b border-border/60 pb-3 last:border-0" data-testid={`report-${r.id}`}>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-white font-medium truncate">{r.title}</span>
                                    <span className="font-mono text-xs text-volt">{r.rating}</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.summary}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
